import React from 'react';
import { useTranslations } from 'src/contexts/TranslationContext';
import CommonPage from 'src/features/about/CommonPage';
import { useParams } from 'react-router';

const Privacy: React.FC = () => {
  const { lang } = useParams<{ lang: string }>();
  const { translations: t, language, isLoading } = useTranslations();
  const [hasTranslations, setHasTranslations] = React.useState(false);
  const [attemptCount, setAttemptCount] = React.useState(0);

  React.useEffect(() => {
    const privacyData = t?.privacy || t?.navigations?.privacy;
    const hasPrivacyTranslations = !!privacyData;
    setHasTranslations(hasPrivacyTranslations);

    if (!isLoading && t && !hasPrivacyTranslations && attemptCount < 3) {
      setAttemptCount(prev => prev + 1);
    }
  }, [t, language, isLoading]);

  React.useEffect(() => {
    if (isLoading) {
      const timeoutId = setTimeout(() => {
        if (isLoading) setHasTranslations(true);
      }, 3000);
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading]);

  React.useEffect(() => {
    setAttemptCount(0);
  }, [language]);

  React.useEffect(() => {
    const forceDisplayTimer = setTimeout(() => {
      setHasTranslations(true);
    }, 5000);
    return () => clearTimeout(forceDisplayTimer);
  }, []);

  if ((isLoading || !hasTranslations) && attemptCount < 3) {
    return (
      <div className="max-w-6xl mx-auto p-8 text-center">
        <h2 className="text-2xl font-semibold mb-4">Loading Privacy Policy...</h2>
        <p className="text-gray-500">Please wait while we load the content in {language}...</p>
        {attemptCount > 0 && (
          <p className="mt-4 text-xs text-gray-400">Attempt {attemptCount + 1}/3...</p>
        )}
      </div>
    );
  }

  const privacyContent = t?.privacy || t?.navigations?.privacy || {};

  const content = (
    <div className="prose max-w-none">

      <h2 className="text-2xl font-semibold mb-4">
        {privacyContent.title || 'Privacy Policy'}
      </h2>

      <p className="mb-4 text-gray-500">
        {privacyContent.lastUpdated || 'Last Updated: May 3, 2025'}
      </p>

      <section className="mb-6">
        <p>{privacyContent.introduction || 'At Global24, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">
          {privacyContent.collectionTitle || 'Information We Collect'}
        </h3>
        <p>{privacyContent.collectionDesc || 'We may collect personal information that you voluntarily provide when interacting with our website, including but not limited to your name, email address, and any other information you choose to provide when registering for an account, subscribing to our newsletter, or contacting us.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">
          {privacyContent.automaticTitle || 'Information Automatically Collected'}
        </h3>
        <p>{privacyContent.automaticDesc || 'When you visit our website, we may automatically collect certain information about your device, including your IP address, browser type, operating system, and browsing patterns. We use cookies and similar tracking technologies to enhance your user experience and analyze website traffic.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">
          {privacyContent.usageTitle || 'How We Use Your Information'}
        </h3>
        <p>We may use the information we collect to:</p>
        <ul className="list-disc pl-6">
          {privacyContent.usageList && Array.isArray(privacyContent.usageList) ? (
            privacyContent.usageList.map((item: string, idx: number) => <li key={idx}>{item}</li>)
          ) : (
            <>
              <li>Provide and maintain our website</li>
              <li>Personalize your experience</li>
              <li>Improve our website and services</li>
              <li>Send periodic emails and newsletters</li>
              <li>Process transactions</li>
              <li>Respond to your inquiries</li>
            </>
          )}
        </ul>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">
          {privacyContent.sharingTitle || 'Disclosure of Your Information'}
        </h3>
        <p>{privacyContent.sharingDesc || 'We do not sell, trade, or otherwise transfer your personally identifiable information to third parties without your consent, except as described in this Privacy Policy.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">
          {privacyContent.cookiesTitle || 'Cookies Policy'}
        </h3>
        <p>{privacyContent.cookiesDesc || 'We use cookies to enhance your browsing experience, analyze website traffic, and personalize content. You can choose to have your computer warn you each time a cookie is being sent, or you can choose to turn off all cookies through your browser settings.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">
          {privacyContent.thirdPartyTitle || 'Third-Party Links'}
        </h3>
        <p>{privacyContent.thirdPartyDesc || 'Our website may contain links to third-party websites that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">
          {privacyContent.childrenTitle || "Children's Privacy"}
        </h3>
        <p>{privacyContent.childrenDesc || 'Our website is not intended for individuals under the age of 13. We do not knowingly collect personal information from children under 13.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">
          {privacyContent.securityTitle || 'Security of Your Information'}
        </h3>
        <p>{privacyContent.securityDesc || 'We use administrative, technical, and physical security measures to help protect your personal information from unauthorized access, use, or disclosure.'}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">
          {privacyContent.changesTitle || 'Changes to This Privacy Policy'}
        </h3>
        <p>{privacyContent.changesDesc || "We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the 'Last Updated' date."}</p>
      </section>

      <section className="mb-6">
        <h3 className="text-xl font-semibold mb-2">
          {privacyContent.contactTitle || 'Contact Information'}
        </h3>
        <p>{privacyContent.contactDesc || 'If you have any questions or concerns about this Privacy Policy, please contact us at privacy@global24.news.'}</p>
      </section>

    </div>
  );

  const pageTitle = privacyContent.title || 'Privacy Policy';
  const pageDescription = privacyContent.introduction || 'Global24 is your trusted source for real-time global news, delivering breaking stories with unmatched speed and accuracy.';

  return (
    <CommonPage
      lang={lang}
      pageType="privacy"
      title={pageTitle}
      metaDescription={pageDescription}
      content={content}
    />
  );
};

export default Privacy;
